// ---------------------------------------------------------------------------
// Trader profile types
// ---------------------------------------------------------------------------
// NOTE: trading_series, balance_series, is_large_account, notable_positions,
// and recent_positions have been removed from TraderProfile.
// Use useTraderSeries() for series data and the /positions endpoint for positions.
// category_metrics was removed and then re-added (v2 migration) directly on
// TraderProfile, under a new dict-keyed shape — see TraderCategoryStats below.
// ---------------------------------------------------------------------------

export interface TraderActivity {
    first_seen: string;
    last_seen: string;
    active_days: number;
    total_trades: number;
    avg_size_usd: number;
    lifetime_volume_usd: number;
    unique_markets: number;
    closed_count: number;
    avg_hold_days: number;
    trade_freq_per_day: number;
    hourly_activity: number[]; // 24 values, index = UTC hour
    size_distribution: { bucket: string; count: number }[];
}

export interface TraderPerformanceMetrics {
    win_rate: number;
    total_pnl: number;
    realized_pnl: number;
    roi_pct: number;
    roe_pct: number | null;
    profit_factor: number;
    daily_sharpe: number | null;
    position_sharpe: number | null;
    max_pnl_drawdown_usd: number | null;
    roct: number;
    twr_pct: number;
}

export interface TraderFinancials {
    balance_usd: number | null;
    portfolio_value: number | null;
    net_deposits: number | null;
    total_deposits: number | null;
    total_withdrawals: number | null;
}

export interface TraderBehaviorSignals {
    algo_likelihood: number;
    repeat_market_rate: number;
    off_hours_activity: number;
    maker_ratio: number;
    avg_entry_prob: number;
    avg_edge: number;
    entry_prob_buckets: number[];
    capital_utilization: number | null;
    market_liquidity_score: number | null;
}

export interface BehaviorInsight {
    title: string;
    body: string;
}

// Dict value under TraderProfile.category_metrics — keyed by category
// (politics, sports, crypto, finance, culture, mentions, weather, economics,
// tech, other). Only categories the trader has activity in are present.
export interface TraderCategoryStats {
    market_count: number;
    closed_count: number;
    win_count: number;
    win_rate: number;
    total_pnl: number;
    realized_pnl: number;
    volume_usd: number;
    profit_factor: number;
    position_sharpe: number | null;
    avg_hold_days: number;
    avg_edge: number;
    roct: number;
    market_liquidity_score: number | null;
}

export interface TraderProfile {
    address: string;
    alias: string | null;
    profile_pic_url: string | null;
    activity: TraderActivity;
    performance: TraderPerformanceMetrics;
    financials: TraderFinancials;
    behavior: TraderBehaviorSignals;
    behavior_insight: BehaviorInsight[];
    category_metrics: Record<string, TraderCategoryStats>;
    tags: string[];
    // Why the Data Error tag is set (bogg's sanity violation codes); empty
    // without the tag. Missing on an indexer that predates it.
    data_issues?: string[];
}

// ---------------------------------------------------------------------------
// Series types (used by useTraderSeries / GET /api/trader/{address}/series)
// ---------------------------------------------------------------------------

export interface TraderTimePoint {
    timestamp: string;
    cumulative_pnl: number | null; // null on volume-only ticks
    volume_usd: number;
}

export interface BalanceTimePoint {
    timestamp: string;
    balance_usd: number;
}

export type SeriesType = 'trading' | 'balance';
export type SeriesResolution = 'auto' | '1d' | '1h';

export interface TraderSeriesResponse {
    series_type: SeriesType;
    // Server-chosen resolution: 'hourly' | 'snapshot' | 'daily' | 'hourly_balance'
    resolution: string;
    points: TraderTimePoint[] | BalanceTimePoint[];
}

// Narrowed helpers so callers don't have to cast
export function isTradingPoints(
    r: TraderSeriesResponse
): r is TraderSeriesResponse & { points: TraderTimePoint[] } {
    return r.series_type === 'trading';
}

export function isBalancePoints(
    r: TraderSeriesResponse
): r is TraderSeriesResponse & { points: BalanceTimePoint[] } {
    return r.series_type === 'balance';
}

// ---------------------------------------------------------------------------
// Position types (used by useTraderPositions / GET /api/trader/{address}/positions)
// These were previously embedded in TraderProfile; now fetched on demand.
// ---------------------------------------------------------------------------

export type PositionCategory =
    | 'politics'
    | 'sports'
    | 'crypto'
    | 'finance'
    | 'culture'
    | 'mentions'
    | 'weather'
    | 'economics'
    | 'tech'
    | 'other';

export const POSITION_CATEGORIES: PositionCategory[] = [
    'politics',
    'sports',
    'crypto',
    'finance',
    'culture',
    'mentions',
    'weather',
    'economics',
    'tech',
    'other',
];

export interface TraderPosition {
    market_question: string;
    condition_id: string;
    event_slug: string | null;
    event_id: string | null;
    category: PositionCategory;
    side: 'YES' | 'NO';
    size_usd: number;
    entry_price: number;
    exit_price: number | null;
    avg_exit_price: number | null;
    current_price: number | null;
    current_value: number | null;
    cost_basis: number | null;
    total_buy_quote: number | null;
    total_sell_quote: number | null;
    realized_pnl: number;
    unrealized_pnl: number;
    hold_days: number | null;
    last_trade_at: string | null;
    pnl: number;
    won: boolean | null;
    open_position: number | null;
    shares_bought: number;
    shares_sold: number;
    buy_count: number | null;
    sell_count: number | null;
}

export type PositionSort =
    | 'notable'
    | 'total_pnl'
    | 'current_value'
    | 'open_position'
    | 'recent';

export interface PositionsPage {
    positions: TraderPosition[];
    total: number;
    offset: number;
    limit: number;
    has_more: boolean;
}
